/**
 * LotSmith — Calendar P&L heatmap (monthly view).
 *
 * Per plan §05-mockups/04 (calendar-pnl mockup), renders the active
 * month as a Mon–Sun grid. Each cell:
 *   - day number top-left
 *   - signed P&L amount centered (profit-green / loss-red)
 *   - trade count subtitle ("2 trades")
 *   - "no trades" / "weekend" placeholder copy on empty days
 *   - blue ring around today, plus "today · live" subtitle
 *
 * Color intensity ∝ |P&L| / month-max(|P&L|), so a small win day
 * looks faintly green next to a heavy win day's saturated green.
 *
 * Prev / next month nav. Loads up to 366 days of buckets from
 * worker-license; client filters to the visible month.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import type { DemoTrade } from "../data/demo.js";
import { useDev } from "../providers/dataContext.js";
import { EaStateBanner } from "../components/EaStateBanner.js";
import { EmptyState, ErrorState, Skeleton } from "../components/States.js";

interface DailyPnl {
  date: string;        // YYYY-MM-DD UTC
  pnl: number;
  tradeCount: number;
  wins: number;
}

const LOAD_DAYS = 366;

export function Calendar() {
  const { activeAccountId, activeAccount, trades } = useDev();
  const [buckets, setBuckets] = useState<DailyPnl[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cursor, setCursor] = useState<{ year: number; month: number }>(() => {
    const t = new Date();
    return { year: t.getUTCFullYear(), month: t.getUTCMonth() };
  });

  useEffect(() => {
    const base = licenseApiBase();
    if (!base || !activeAccountId) {
      setError(null);
      setIsLoading(false);
      setBuckets(buildDailyBucketsFromTrades(trades));
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await fetch(`${base}/v1/accounts/${activeAccountId}/calendar?days=${LOAD_DAYS}`, {
          credentials: "include",
          headers: { accept: "application/json" },
        });
        if (cancelled) return;
        if (!r.ok) throw new Error(`calendar ${r.status}`);
        const j = await r.json() as { buckets: DailyPnl[] };
        setBuckets(j.buckets);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Couldn't load calendar.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeAccountId, trades]);

  const grid = useMemo(() => buildMonthGrid(cursor.year, cursor.month, buckets ?? []), [cursor, buckets]);
  const monthMax = useMemo(() => grid.maxAbsPnl, [grid]);

  if (error) {
    return (
      <div className="dash">
        <RouteHeading activeAccount={activeAccount} />
        <ErrorState title="Couldn't load calendar" body={error} cta={{ label: "Retry", onClick: () => location.reload() }} />
      </div>
    );
  }
  if (isLoading || buckets === null) {
    return (
      <div className="dash">
        <RouteHeading activeAccount={activeAccount} />
        <section className="panel"><div className="panel__body"><Skeleton height="22px" lines={6} /></div></section>
      </div>
    );
  }
  if (buckets.length === 0) {
    return (
      <div className="dash">
        <RouteHeading activeAccount={activeAccount} />
        <section className="panel empty-panel">
          <EmptyState
            title="No calendar history yet"
            body="The heatmap appears after the EA syncs closed trades. Until then, use Trades for journal setup and Onboarding to verify the terminal install."
          />
          <div className="empty-actions">
            <Link to="/app/trades" className="btn btn--ghost">Open trades</Link>
            <Link to="/onboarding" className="btn btn--primary">EA setup</Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="dash">
      <RouteHeading activeAccount={activeAccount} netPnl={grid.netPnl} winningDays={grid.winningDays} losingDays={grid.losingDays} />

      {activeAccount?.eaState && (
        <EaStateBanner
          state={activeAccount.eaState}
          reason={activeAccount.eaStateReason}
          since={activeAccount.eaStateUpdatedAt}
        />
      )}

      <section className="panel cal-month">
        <div className="cal-month__hdr">
          <div>
            <h2 className="panel__title">{monthLabel(cursor.year, cursor.month)}</h2>
            <p className="cal-month__summary">
              {formatSignedMoney(grid.netPnl)} net
              {" · "}{grid.winningDays} winning {pl(grid.winningDays, "day", "days")}
              {" · "}{grid.losingDays} losing {pl(grid.losingDays, "day", "days")}
              {" · "}{grid.noTradeDays} no-trade {pl(grid.noTradeDays, "day", "days")}
            </p>
          </div>
          <div className="cal-month__nav" role="group" aria-label="Calendar navigation">
            <button
              type="button"
              className="cal-month__navbtn"
              onClick={() => setCursor((prev) => stepMonth(prev, -1))}
              aria-label="Previous month"
            >‹</button>
            <button
              type="button"
              className="cal-month__navbtn"
              onClick={() => setCursor((prev) => stepMonth(prev, 1))}
              aria-label="Next month"
            >›</button>
          </div>
        </div>

        <div className="cal-month__weekhdr">
          {WEEK_LABELS.map((w) => <div key={w} className="cal-month__weeklabel">{w}</div>)}
        </div>

        <div className="cal-month__grid">
          {grid.cells.map((cell, i) => <DayCell key={i} cell={cell} monthMax={monthMax} />)}
        </div>

        <div className="cal-month__legend">
          <span className="cal-month__legend-text">Loss</span>
          <span className="cal-month__legend-swatch cal-month__legend-swatch--loss-3" />
          <span className="cal-month__legend-swatch cal-month__legend-swatch--loss-1" />
          <span className="cal-month__legend-swatch cal-month__legend-swatch--empty" />
          <span className="cal-month__legend-swatch cal-month__legend-swatch--profit-1" />
          <span className="cal-month__legend-swatch cal-month__legend-swatch--profit-3" />
          <span className="cal-month__legend-text">Profit</span>
          <span className="cal-month__legend-text cal-month__legend-text--right">Click any day for trade detail</span>
        </div>
      </section>
    </div>
  );
}

function RouteHeading({
  activeAccount,
  netPnl,
  winningDays,
  losingDays,
}: {
  activeAccount: ReturnType<typeof useDev>["activeAccount"];
  netPnl?: number;
  winningDays?: number;
  losingDays?: number;
}) {
  return (
    <section className="route-hero">
      <div>
        <div className="dash__eyebrow">
          {activeAccount ? `${activeAccount.propFirm} · #${activeAccount.brokerLogin}` : "Calendar"}
        </div>
        <h1 className="dash__title">Calendar</h1>
        <p className="route-hero__lead">Daily P&amp;L heatmap for spotting overtrading clusters and rule-risk weeks.</p>
      </div>
      {typeof netPnl === "number" && (
        <div className="route-hero__stats">
          <div className="route-stat">
            <span>Month P&amp;L</span>
            <strong className={netPnl >= 0 ? "tone-profit" : "tone-loss"}>{formatSignedMoney(netPnl)}</strong>
          </div>
          <div className="route-stat">
            <span>Winning days</span>
            <strong>{winningDays ?? 0}</strong>
          </div>
          <div className="route-stat">
            <span>Losing days</span>
            <strong>{losingDays ?? 0}</strong>
          </div>
        </div>
      )}
    </section>
  );
}

const WEEK_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface DayCellModel {
  date: string;            // YYYY-MM-DD UTC
  inMonth: boolean;
  isWeekend: boolean;
  isToday: boolean;
  pnl: number | null;
  trades: number;
}

interface MonthGrid {
  cells: DayCellModel[];
  maxAbsPnl: number;
  netPnl: number;
  winningDays: number;
  losingDays: number;
  noTradeDays: number;
}

function buildMonthGrid(year: number, month: number, buckets: DailyPnl[]): MonthGrid {
  const map = new Map<string, DailyPnl>();
  for (const b of buckets) map.set(b.date, b);

  const todayIso = todayUtcIso();
  const firstOfMonth = new Date(Date.UTC(year, month, 1));
  // Mon=0, Sun=6
  const offset = (firstOfMonth.getUTCDay() + 6) % 7;

  // Render exactly 35 cells (5 weeks) — most months fit. Months that
  // span 6 weeks get a 6th row on the fly.
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const totalCells = Math.ceil((offset + lastDay) / 7) * 7;

  const cells: DayCellModel[] = [];
  let maxAbs = 0;
  let net = 0;
  let win = 0;
  let loss = 0;
  let none = 0;

  for (let i = 0; i < totalCells; i++) {
    const dayN = i - offset + 1;
    const inMonth = dayN >= 1 && dayN <= lastDay;
    const d = new Date(Date.UTC(year, month, dayN));
    const iso = d.toISOString().slice(0, 10);
    const isWeekend = d.getUTCDay() === 0 || d.getUTCDay() === 6;
    const hit = map.get(iso);
    const pnl = hit ? hit.pnl : null;
    const trades = hit ? hit.tradeCount : 0;
    cells.push({
      date: iso,
      inMonth,
      isWeekend,
      isToday: iso === todayIso,
      pnl,
      trades,
    });
    if (inMonth) {
      const abs = pnl === null ? 0 : Math.abs(pnl);
      if (abs > maxAbs) maxAbs = abs;
      if (pnl === null || trades === 0) none++;
      else if (pnl > 0) { win++; net += pnl; }
      else if (pnl < 0) { loss++; net += pnl; }
      else net += pnl;
    }
  }

  return { cells, maxAbsPnl: maxAbs || 1, netPnl: net, winningDays: win, losingDays: loss, noTradeDays: none };
}

function buildDailyBucketsFromTrades(trades: DemoTrade[]): DailyPnl[] {
  const byDate = new Map<string, DailyPnl>();
  for (const trade of trades) {
    const date = trade.ts.slice(0, 10);
    const current = byDate.get(date) ?? { date, pnl: 0, tradeCount: 0, wins: 0 };
    current.pnl += trade.pnl;
    current.tradeCount += 1;
    if (trade.pnl > 0) current.wins += 1;
    byDate.set(date, current);
  }
  return Array.from(byDate.values())
    .map((bucket) => ({ ...bucket, pnl: Math.round(bucket.pnl * 100) / 100 }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

function DayCell({ cell, monthMax }: { cell: DayCellModel; monthMax: number }) {
  if (!cell.inMonth) {
    return <div className="cal-month__cell cal-month__cell--out" aria-hidden />;
  }
  const dayNum = Number(cell.date.slice(8, 10));
  const tone = cell.pnl === null || cell.trades === 0
    ? "empty"
    : cell.pnl > 0 ? "profit" : cell.pnl < 0 ? "loss" : "flat";

  // Bucket intensity into 3 levels so the visual reads as discrete
  // steps rather than a noisy gradient (matches the mockup palette).
  const ratio = cell.pnl === null ? 0 : Math.abs(cell.pnl) / monthMax;
  const level = ratio >= 0.66 ? 3 : ratio >= 0.33 ? 2 : ratio > 0 ? 1 : 0;

  const cls = [
    "cal-month__cell",
    `cal-month__cell--${tone}`,
    level > 0 ? `cal-month__cell--lv-${level}` : "",
    cell.isToday ? "cal-month__cell--today" : "",
    cell.isWeekend && tone === "empty" ? "cal-month__cell--weekend" : "",
  ].filter(Boolean).join(" ");

  return (
    <button type="button" className={cls} title={tooltipFor(cell)}>
      <div className="cal-month__cellnum">{dayNum}</div>
      {cell.trades > 0 && cell.pnl !== null ? (
        <>
          <div className={`cal-month__cellpnl tone-${tone}`}>{formatSignedMoney(cell.pnl)}</div>
          <div className="cal-month__celltrades">{cell.trades} {pl(cell.trades, "trade", "trades")}</div>
        </>
      ) : (
        <div className="cal-month__cellplaceholder">
          {cell.isToday ? "today · live" : (cell.isWeekend ? "weekend" : "no trades")}
        </div>
      )}
    </button>
  );
}

function tooltipFor(cell: DayCellModel): string {
  if (cell.pnl === null || cell.trades === 0) return `${cell.date} · no trades`;
  return `${cell.date} · ${formatSignedMoney(cell.pnl)} · ${cell.trades} ${pl(cell.trades, "trade", "trades")}`;
}

function monthLabel(year: number, month: number): string {
  const d = new Date(Date.UTC(year, month, 1));
  return d.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

function stepMonth(prev: { year: number; month: number }, dir: -1 | 1): { year: number; month: number } {
  const m = prev.month + dir;
  if (m < 0)  return { year: prev.year - 1, month: 11 };
  if (m > 11) return { year: prev.year + 1, month: 0 };
  return { year: prev.year, month: m };
}

function todayUtcIso(): string {
  const t = new Date();
  t.setUTCHours(0, 0, 0, 0);
  return t.toISOString().slice(0, 10);
}

function formatSignedMoney(v: number): string {
  if (!Number.isFinite(v) || v === 0) return "$0";
  const sign = v >= 0 ? "+" : "−";
  const abs = Math.abs(v);
  return `${sign}$${abs.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
}

function pl(n: number, singular: string, plural: string): string {
  return n === 1 ? singular : plural;
}

function licenseApiBase(): string {
  return (import.meta.env.VITE_LICENSE_API_BASE as string | undefined) ?? "";
}
